import { useEffect } from 'react'
import { useAppDispatch, useAppSelector } from '../../redux/hooks'
import { ChatType, IWebComponentProps } from '../../types/webComponent'
import Messages from './Messages'
import { loginSuccess } from '../../redux/reducers/authSlice'
import { User } from '../../types/api'
import {
  setDropdownContainer,
  setIsChatList,
  setModalContainer,
  setTemplateLayoutData,
} from '../../redux/reducers/templateCustomizerSlice'
import { queries } from '../../api'
import { setSetting } from '../../redux/reducers/settingSlice'
import { clearScreen } from '../../redux/reducers/messenger/screenSlice'
import ChatList from './ChatList'
import { resetMainSidebar } from '../../redux/reducers/messenger/mainSidebarSlice'
import { resetChat, resetChatState, setIsNewChatOpen, setOpenStory } from '../../redux/reducers/messenger/chatSlice'
import { resetMessageSelection } from '../../redux/reducers/messenger/messageSelectionSlice'
import { resetMessenger } from '../../redux/reducers/messenger/messengerSlice'
import { STORAGE_KEYS } from '../../constants/storageKeys'
import { getStorage } from '../../utils'
import useDeepLinkChat from '../../hooks/useDeepLinkChat'
import useAutoSelectFirstChat from '../../hooks/useAutoSelectFirstChat'
//import NotificationService from '../../services/notification-service';

const ChatWrapper = (props: IWebComponentProps) => {
  const {
    user,
    token,
    serviceToken,
    type,
    selectChatId,
    selectChatType,
    modalContainer,
    dropdownContainer,
    onResetUnread,
    onLoading,
    onViewProfile,
    onOpenStory,
  } = props
  const dispatch = useAppDispatch()

  // Deep link from a push notification: open the requested conversation once the
  // chat list has loaded.
  useDeepLinkChat(selectChatId, selectChatType)
  // With no deep link, open the top conversation by default so the Messages page
  // never sits on the empty "Select a chat to start messaging" state.
  useAutoSelectFirstChat(type, selectChatId)
  const { data: settingData } = queries.useGetSettings(token)
  const { data: publicSettingData } = queries.useGetPublicSettings()
  const { isUnread, isNewChatOpen, viewProfile, openStory } = useAppSelector((state) => state.chat)
  const storage = getStorage()

  useEffect(() => {
    return () => {
      // Only reset state on navigation for Messages (full-page embedded chat)
      // Skip cleanup for CHAT_LIST (sticky widget) to preserve state across navigation
      if (type !== ChatType.CHAT_LIST) {
        dispatch(resetChat())
        dispatch(resetChatState())
        dispatch(resetMessageSelection())
        dispatch(resetMainSidebar())
        dispatch(resetMessenger())
        storage.removeItem(STORAGE_KEYS.SELECTED_USER)
        storage.removeItem(STORAGE_KEYS.SELECTED_CHAT)
        storage.removeItem(STORAGE_KEYS.CURRENT_SCREEN)
        storage.removeItem(STORAGE_KEYS.SERVICE_TOKEN)
      }
    }
  }, [location.pathname, dispatch, type])

  useEffect(() => {
    const handler = () => {
      dispatch(clearScreen())
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', handler)
      return () => {
        window.removeEventListener('beforeunload', handler)
      }
    }
  }, [])

  useEffect(() => {
    if (viewProfile && onViewProfile) {
      onViewProfile(viewProfile)
    }
  }, [viewProfile])

  // Hand the story off to the page hosting the widget, which opens it as a
  // modal without navigating away. Cleared straight away so tapping the same
  // story again re-opens it.
  useEffect(() => {
    if (!openStory) return
    dispatch(setOpenStory(null))
    if (onOpenStory) {
      onOpenStory(openStory)
    } else {
      window.open(`/stories/${openStory}`, '_blank', 'noopener,noreferrer')
    }
  }, [openStory])

  useEffect(() => {
    if (isNewChatOpen && onLoading) {
      dispatch(setIsNewChatOpen(false))
      onLoading(null)
    }
  }, [isNewChatOpen])

  useEffect(() => {
    if (publicSettingData && !token) {
      dispatch(setSetting(publicSettingData))
    }
  }, [publicSettingData, token, dispatch])

  useEffect(() => {
    if (settingData && token) {
      dispatch(setSetting(settingData))
    }
  }, [settingData, token, dispatch])

  useEffect(() => {
    if (type && type === ChatType.CHAT_LIST) {
      dispatch(setIsChatList(true))
    }

    if (type && type === ChatType.MESSAGES) {
      dispatch(setIsChatList(false))
    }
  }, [type])

  useEffect(() => {
    const savedLayout = localStorage.getItem('templateLayout')
    if (savedLayout) {
      try {
        const parsed = JSON.parse(savedLayout)
        if (parsed.layoutValue) {
          dispatch(setTemplateLayoutData(parsed.layoutValue))
        }
      } catch (error) {
        console.error('Failed to parse saved layout:', error)
      }
    }
  }, [dispatch])

  // useEffect(() => {
  //   NotificationService.initialize();
  // }, [])

  useEffect(() => {
    // `isUnread` can legitimately be 0 (all messages read). Guard on it being a
    // number instead of truthiness, otherwise the all-read case is never emitted
    // and the host badge never resets to 0 without a reload.
    if (onResetUnread && typeof isUnread === 'number') {
      onResetUnread(isUnread)
    }
  }, [isUnread])

  useEffect(() => {
    if (modalContainer) {
      dispatch(setModalContainer(modalContainer))
    }
  }, [modalContainer])

  useEffect(() => {
    if (dropdownContainer) {
      dispatch(setDropdownContainer(dropdownContainer))
    }
  }, [dropdownContainer])

  useEffect(() => {
    if (user && token) {
      ;(window as any).baseUrl = import.meta.env.VITE_SERVER_BASE_URL
      dispatch(
        loginSuccess({
          token: token as string,
          user: user as User,
        }),
      )
    }
  }, [token, user])

  // Persist the main-platform token used to authenticate
  useEffect(() => {
    if (serviceToken) {
      storage.setItem(STORAGE_KEYS.SERVICE_TOKEN, serviceToken)
    } else {
      storage.removeItem(STORAGE_KEYS.SERVICE_TOKEN)
    }
  }, [serviceToken])

  switch (type) {
    case ChatType.MESSAGES:
      return <Messages />
    case ChatType.CHAT_LIST:
      return <ChatList {...props} />
    default:
      return <Messages />
  }
}

export default ChatWrapper
